import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Block as DisableIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  AccountBalance as LedgerIcon,
} from '@mui/icons-material'
import { useCustomer, useDisableCustomer } from '../../hooks/api/useCustomers'
import { format } from 'date-fns'
import CustomerForm from '../../components/forms/CustomerForm'
import StiRatesModal from '../../components/forms/StiRatesModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

const CustomerDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [stiRatesOpen, setStiRatesOpen] = useState(false)

  const { data: customer, isLoading, error } = useCustomer(id!)
  const disableCustomerMutation = useDisableCustomer()
  const { user } = useSelector((state: RootState) => state.auth)
  const stiFeatureEnabled = user?.company_features?.sales_tax_invoice ?? false
  const cartonDiscountPricingEnabled = user?.company_features?.carton_discount_pricing === true
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    setFormOpen(true)
    handleMenuClose()
  }

  const handleDelete = () => {
    setDeleteDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteConfirm = () => {
    if (customer) {
      disableCustomerMutation.mutate(customer._id, {
        onSuccess: () => {
          navigate('/customers')
        }
      })
    }
    setDeleteDialogOpen(false)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  const handleFormClose = () => {
    setFormOpen(false)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !customer) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error ? 'Failed to load customer details' : 'Customer not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/customers')}>
            <ArrowBackIcon />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4">{customer.name}{customer.customer_code ? ` - ${customer.customer_code}` : ''}</Typography>
            {customer.is_sales_tax_customer && (
              <StatusChip label="STI Customer" variant="warn" />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<LedgerIcon />}
            onClick={() => navigate(`/customers/${id}/ledger`)}
            size="small"
          >
            Ledger
          </Button>
          {stiFeatureEnabled && customer.is_sales_tax_customer && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ReceiptIcon />}
              onClick={() => setStiRatesOpen(true)}
              size="small"
            >
              Manage STI Rates
            </Button>
          )}
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Area
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.area_id?.area_name || 'Not provided'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Customer Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  CNIC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.cnic || 'Not provided'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.phone || 'Not provided'}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.address || 'Not provided'}
                </Typography>
              </Grid>
              
              {advanceTaxEnabled && <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Filer Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <StatusChip
                    label={customer.is_filer ? 'Filer' : 'Non-Filer'}
                    variant={customer.is_filer ? 'success' : 'neutral'}
                  />
                </Box>
              </Grid>}
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {cartonDiscountPricingEnabled ? 'Customer Type' : 'Discount Percentage'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {cartonDiscountPricingEnabled
                    ? customer.customer_type === 'wholesale' ? 'Wholesale' : 'Retail'
                    : `${customer.discount}%`}
                </Typography>
              </Grid>
              
              {advanceTaxEnabled && <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Advance Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {customer.adv_tax}%
                </Typography>
              </Grid>}
            </Grid>
          </Paper>

          {/* Sales Tax Invoice Info */}
          {stiFeatureEnabled && customer.is_sales_tax_customer && (
            <Paper sx={{ p: 3, mt: 3, border: '1px solid', borderColor: 'warning.main' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ReceiptIcon color="warning" fontSize="small" />
                <Typography variant="h6" color="warning.dark">
                  Sales Tax Invoice (STI) Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    STRN
                  </Typography>
                  <Typography variant="body1">
                    {customer.strn || <em style={{ color: '#999' }}>Not provided</em>}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    NTN
                  </Typography>
                  <Typography variant="body1">
                    {customer.ntn || <em style={{ color: '#999' }}>Not provided</em>}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    STI Rates Configured
                  </Typography>
                  <Typography variant="body1">
                    {customer.sales_tax_rates && customer.sales_tax_rates.length > 0
                      ? `${customer.sales_tax_rates.length} SKU(s) with custom rates`
                      : 'No custom rates set yet'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<ReceiptIcon />}
                    onClick={() => setStiRatesOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    Manage STI Rates
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Customer ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {customer._id}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Created Date
              </Typography>
              <Typography variant="body2">
                {format(new Date(customer.createdAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {format(new Date(customer.updatedAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Customer
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'warning.main' }}>
          <DisableIcon sx={{ mr: 1 }} />
          Disable Customer
        </MenuItem>
      </Menu>

      <CustomerForm
        open={formOpen}
        onClose={handleFormClose}
        customer={customer}
        mode="edit"
      />

      {stiFeatureEnabled && customer.is_sales_tax_customer && (
        <StiRatesModal
          open={stiRatesOpen}
          onClose={() => setStiRatesOpen(false)}
          customerId={customer._id}
          customerName={customer.name}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Disable Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Disable the customer "{customer?.name}"? Existing bills will be preserved, but the customer cannot be used for new bookings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="warning" variant="contained">
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerDetailsPage
